import {
  Box,
  Center,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import siteImageUploadImg from '../../../assets/images/onboarding_upload_site_image.svg';
import { CloseIcon } from '@chakra-ui/icons';

export type UploadedFile = {
  url: string;
  isPersisted: boolean;
  fileData: ArrayBuffer | null; // Only relevent for non-persisted images
  extension: string;
};

type UploadTenantLogoProps = {
  onUploadImage: (fileDataArrayBuffer: ArrayBuffer, extension: string) => void | Promise<void>;
  onDeleteFile: (file: UploadedFile) => void | Promise<void>;
  logoFile: UploadedFile | null;
  isUploading?: boolean;
};

const INITIAL_MODAL_CONFIG = { isModalOpen: false, imageUrl: '', isImageLoaded: false };

export default function UploadTenantLogo({
  onUploadImage,
  logoFile,
  onDeleteFile,
  isUploading,
}: UploadTenantLogoProps) {
  const [state, setState] = useState(INITIAL_MODAL_CONFIG);
  const toast = useToast();

  const handleFileUpload = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onabort = () => {
          toast({
            title: 'Error reading file.',
            description: 'Please try uploading this file again.',
            status: 'error',
            isClosable: true,
          });
          console.log('file reading was aborted');
        };
        reader.onerror = () => {
          toast({
            title: 'Error reading file.',
            description: 'Please try uploading this file again.',
            status: 'error',
            isClosable: true,
          });
          console.log('file reading has failed');
        };
        reader.onload = async () => {
          const fileData = reader.result as ArrayBuffer;
          const extension = file.name.split('.').pop() as string;
          onUploadImage(fileData, extension);
        };

        reader.readAsArrayBuffer(file);
      });
    },
    [toast, onUploadImage]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileUpload,
    accept: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp', 'image/gif', 'image/apng', 'image/avif'],
    multiple: false,
  });

  const noFilesContents = (
    <Center
      my={5}
      justifySelf={'center'}
      flexDirection="column"
      border="2px"
      borderStyle="dashed"
      rounded={8}
      borderColor="day.500"
      bg="rgba(134, 181, 209, 0.1)"
      minH="155px"
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Image mb={4} src={siteImageUploadImg} alt="Upload icon" w={65} />
      <Text px={2} textAlign="center" mb={2} fontSize="sm" color="dusk050.500">
        Transparent PNG with 4/1 aspect ratio recommended
      </Text>
    </Center>
  );

  const existingFilesContents = (
    <Box my={4} minH="155px">
      <Modal
        isOpen={state.isModalOpen}
        onClose={() => {
          setState(INITIAL_MODAL_CONFIG);
        }}
        allowPinchZoom
        isCentered
      >
        <ModalOverlay />
        <ModalContent rounded={10} overflow="hidden">
          <ModalCloseButton bg={'white'} color={'black'} rounded={20} />
          <ModalBody p={0}>
            <Center minH="60vh">
              {!state.isImageLoaded && (
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
              )}
              <Image
                shadow="md"
                display={state.isImageLoaded ? 'initial' : 'none'}
                w="100%"
                h="100%"
                objectFit={'cover'}
                src={state.imageUrl}
                alt={'file'}
                onLoad={() => setState((prevProps) => ({ ...prevProps, isImageLoaded: true }))}
              />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>

      {logoFile && (
        <Flex mt={6}>
          <Center w={200} position="relative">
            <IconButton
              icon={isUploading ? <Spinner color="white" size="md" /> : <CloseIcon fontSize="md" color="white" />}
              aria-label="Cancel"
              isDisabled={isUploading}
              onClick={() => onDeleteFile(logoFile)}
              minW={12}
              maxH={12}
              variant="solid"
              bg="#333333"
              _hover={{ bg: 'black' }}
              shadow={'lg'}
              rounded={100}
              position="absolute"
              top={-3}
              right={-3}
              zIndex={2}
            />
            <Image
              w="100%"
              cursor={'pointer'}
              rounded={2}
              objectFit={'cover'}
              src={logoFile.url}
              alt={'file'}
              onClick={() => setState({ isModalOpen: true, imageUrl: logoFile.url, isImageLoaded: false })}
            />
          </Center>
        </Flex>
      )}
    </Box>
  );

  return <Box className="override-parent">{logoFile ? existingFilesContents : noFilesContents}</Box>;
}
